
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import Card from '../../../../components/Card';

import { getPropertyInfo } from '../../../../services/api/terminalContract';
import { ApiError, TerminalContract } from '../../../../services/openapi';
import { getTerminalContractsByTerminalId as getTerminalContractsByTerminalId } from '../../../../services/api/terminalContract';
import { useKeycloak } from '@react-keycloak/web';

import ListPagination from '../../../../components/ListPagination';
import RefreshPull from '../../../../components/RefreshPull';
import { Link, useNavigate } from 'react-router-dom';
import { TerminalContracts } from '../../../../features/terminals';
import { useTranslation } from 'react-i18next'; 

import { useTerminalContractsDraft } from '../../../../features/TerminalContracts/TerminalContractsDraftContext';
import { DraftTerminalContract, hasContractChanged, TerminalContractDraftData } from '../../../../features/TerminalContracts/TerminalContractDraft';
import { updateTerminalContract } from '../../../../services/api/terminalContract';
import { NullLogger } from '@microsoft/signalr';
import { PropertyInfo } from '../../../../services/openapi/models/PropertyInfo';
import { NavContext } from '../../../../components/dto/NavContext';
import { resetNavContext } from '../../../../hooks/navContext';

const LinkContractToTerminal = () => {
    const [error, setError] = useState<ApiError | null>();
    const [contracts, setContracts] = useState<TerminalContract[]>([]);
    const [newContract, setNewContract] = useState<TerminalContract>();
    const [formData, setFormData] = useState<string>("");
    const [searchData, setSearchData] = useState<Record<string, string>>({});
    const [contractCount, setContractCount] = useState<string>("0");
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [pagingString, setPagingString] = useState<string>("0-25");
    const [filterData, setFilterData] = useState<string>("");    
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [terminal, setTerminal] = useState<any>(JSON.parse(localStorage.getItem('Terminal') ?? ""));        
    const [tableBodyHeight, setTableBodyHeight] = useState<number>(380);
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [selectedId, setSelectedId] = useState(null);
    const [rowClickCount, setRowClickCount] = useState({});


    const { keycloak, initialized } = useKeycloak();
    const token = keycloak.token || '';

    const navigate = useNavigate();
    const { t } = useTranslation();

    const { drafts, upsertDraft, setInitialDrafts, addNewDraft, clearDrafts, getUsedBrands } = useTerminalContractsDraft();

    const dummyContract: TerminalContract = {
        id: 0,
        terminalId: 0,
        poi: "",
        terminalDescription: "",
        brand: "",
        tid: "",
        mid: "",
        host: "",
        isActive: "False",
        warnings: null
    }

    const showErrorMessages = (errors: string[] | void) => {
        if (errors) {
            errors.forEach((msg: string) => { errorMsg.push(msg); })
        }

        document.querySelector('.errorMsg')?.classList.remove("d-none");
        setIsRefreshing(false);
    }

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const navigateBack = () => {
        navigate('/dashboard/details/terminal');
    };

    const setTerminalContractData = (data: TerminalContract) => {
        localStorage.setItem('TerminalContract', JSON.stringify(data));
    }

    const handleFilter = (e: boolean): void => {        
        setContracts([]);
    }

    const addContract = (data: TerminalContract): void => {
        if (data === undefined) {
            data = dummyContract;
        }
        
        data.poi = terminal.poi;
        data.terminalDescription = terminal.terminalDescription;
        data.terminalId = terminal.id;

        if (data.propertyNames === undefined) {
            getPropertyInfo(keycloak.token || '', data.poi).then((result) => {
                data.propertyNames = result;
                setTerminalContractData(data);
            });
        }
        else {
            setTerminalContractData(data!);
        }
    }

    // "All brands must use the same MID/TID for one host": the first
    // contract seen for a host establishes the comparison value for that
    // field, and any other draft for that host with a different value is
    // flagged.
    const findFirstMismatch = (
        field: 'mid' | 'tid',
        label: string
    ): Map<string, string> => {
        const firstContractByHost = new Map<
            string,
            {
                value: string;
                host: string;
                draftKey: string;
            }
        >();

        const affectedDrafts: typeof drafts = [];

        for (const draft of drafts) {
            const host = (draft.current.host ?? '').trim();
            const value = (draft.current[field] ?? '').trim();

            if (!host || !value) {
                continue;
            }

            const hostKey = host.toUpperCase();
            const valueKey = value.toUpperCase();

            const firstContract = firstContractByHost.get(hostKey);

            // The first contract establishes the comparison value
            if (!firstContract) {
                firstContractByHost.set(hostKey, {
                    value,
                    host,
                    draftKey: draft.key
                });

                continue;
            }

            if (firstContract.value.toUpperCase() !== valueKey) {
                affectedDrafts.push(draft);
            }
        }

        const messagesByKey = new Map<string, string>();

        for (const draft of affectedDrafts) {
            const firstContract = firstContractByHost.get(
                (draft.current.host ?? '').trim().toUpperCase()
            );

            if (!firstContract) {
                continue;
            }

            const draftValue = (draft.current[field] ?? '').trim();

            messagesByKey.set(
                draft.key,
                `Requested parameter value: [${label}: ${draftValue}]. The ${label} '${firstContract.value}' is previously used for this host ` +
                `'${firstContract.host}', all brands must use the same ${label}`
            );
        }

        return messagesByKey;
    };

    const validateMids = (): Map<string, string> | null => {
        const midMismatches = findFirstMismatch('mid', 'MID');
        const tidMismatches = findFirstMismatch('tid', 'TID');

        if (midMismatches.size === 0 && tidMismatches.size === 0) {
            return null;
        }

        const messagesByKey = new Map<string, string>();

        for (const key of new Set([...midMismatches.keys(), ...tidMismatches.keys()])) {
            const messages = [midMismatches.get(key), tidMismatches.get(key)].filter(
                (message): message is string => !!message
            );
            messagesByKey.set(key, messages.join('; '));
        }

        return messagesByKey;
    };

    const handleAddContract = () => {
        const draft = addNewDraft(terminal.id ?? 0, terminal.poi, terminal.description);
        localStorage.setItem('TerminalContractDraftKey', draft.key);
        navigate('/dashboard/details/terminal-contract');
    };

    const handleSaveAll = async () => {
        const fieldMismatches = validateMids();

        // Apply the validation outcome locally so the payload below reflects
        // the forced isActive/warnings values immediately, instead of waiting
        // for the async setDrafts state update triggered by upsertDraft.
        const updatedDrafts = drafts.map(draft => {
            const warningMessage = fieldMismatches?.get(draft.key);

            if (!warningMessage) {
                return draft;
            }

            return {
                ...draft,
                current: {
                    ...draft.current,
                    isActive: "False",
                    warnings: warningMessage
                }
            };
        });

        const draftsToSave = updatedDrafts.filter(
            draft => hasContractChanged(draft) || fieldMismatches?.has(draft.key)
        );

        if (draftsToSave.length === 0) {
            return;
        }

        // Keep the draft context/localStorage in sync with the forced changes.
        updatedDrafts.forEach(draft => {
            if (fieldMismatches?.has(draft.key)) {
                upsertDraft(draft);
            }
        });

        setIsRefreshing(true);

        // Only surface the spinner once saving has taken a while, so a
        // fast (sub-3s) save never shows it at all.
        const spinnerTimer = setTimeout(() => {
            document.querySelector('#refresh')?.classList.remove("d-none");
        }, 3000);

        try {
            for (const draft of draftsToSave) {
                const payload: TerminalContract = {
                    id: draft.current.id ?? 0,
                    terminalId: draft.current.terminalId ?? 0,
                    poi: draft.current.poi ?? '',
                    terminalDescription: draft.current.terminalDescription ?? '',
                    brand: draft.current.brand ?? '',
                    tid: draft.current.tid ?? '',
                    mid: draft.current.mid ?? '',
                    host: draft.current.host ?? '',
                    propertyNames: null,
                    isActive: draft.current.isActive,
                    warnings: draft.current.warnings ?? null
                };

                await updateTerminalContract(
                    keycloak.token || '',
                    payload.terminalId,
                    payload
                );
            }

            clearDrafts();
            navigate('/dashboard/details/terminal');
        } catch (error) {
            const apiError = error as ApiError;

            try {
                const errorsFromBody = JSON.parse(apiError.body)?.errors ?? {} as {
                    errors?: Record<string, string[]>;
                };

                const errors = errorsFromBody.errors ?? {};
                const messages: string[] = [];

                Object.entries(errors).forEach(
                    ([, messagesForProperty]: [string, string[]]) => {
                        messages.push(...messagesForProperty);
                    }
                );

                showErrorMessages(
                    messages.length > 0
                        ? messages
                        : ['An error occurred while saving the terminal contracts.']
                );
            } catch {
                showErrorMessages(['Error handling response']);
            }
        } finally {
            clearTimeout(spinnerTimer);
            document.querySelector('#refresh')?.classList.add("d-none");
            setIsRefreshing(false);
        }
    };

    const handleCancel = () => {
        clearDrafts();
        navigate('/dashboard/details/terminal');
    };

    const handleSearchColumn1 = (e: string): void => {
        setContracts([]);
        searchData["description"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleRefreshButton = (): void => {
        setContracts([]);
        filterData === "" ? setFilterData(" ") : setFilterData("");
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
    }

    const handleRowClick = (draft) => {
            if (window.matchMedia('(pointer: coarse)').matches) {
                setRowClickCount({ [draft.key]: 1 });
            }
            
            setRowClickCount((prev) => {
                const navContext: NavContext = {
                    from: 'lists/terminals',
                    id: null
                };
    
                resetNavContext(navContext);
    
                const newCount = (prev[draft.key] || 0) + 1;
                if (newCount === 1) {
                    setSelectedId(draft.key);
                return { ...prev, [draft.key]: newCount };
                } else if (newCount === 2) {
                    localStorage.setItem('TerminalContractDraftKey', draft.key)
                    navigate("/dashboard/details/terminal-contract");
                    setSelectedId(null);
                    return { ...prev, [draft.key]: 0 };
                }
                return prev;
            });
        };

    const handlePaging = async (e: number, isCancelled: () => boolean = () => false) => {
        do {
            await delay(1000);
        } while (isRefreshing);

        if (isCancelled()) {
            return;
        }

        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        if (terminal.id) {
            setIsRefreshing(true);

            // Only surface the spinner once loading has taken a while, so a
            // fast (sub-3s) load never shows it at all.
            const spinnerTimer = setTimeout(() => {
                if (!isCancelled()) {
                    document.querySelector('#refresh')?.classList.remove("d-none");
                }
            }, 3000);

            getTerminalContractsByTerminalId(keycloak.token || '', terminal.id)
            .then((contrcts: TerminalContract[]) => {
                clearTimeout(spinnerTimer);
                if (isCancelled()) {
                    return;
                }
                setIsRefreshing(false);
                document.querySelector('#refresh')?.classList.add("d-none");
                if (contrcts) {
                    setContracts(contrcts);
                    setInitialDrafts(contrcts);
                }
                })
            .catch((error: ApiError) => {
                clearTimeout(spinnerTimer);
                if (isCancelled()) {
                    return;
                }
                try {
                    document.querySelector('.errorMsg')?.classList.remove('d-none');
                    const errors = JSON.parse(error.body)?.errors ?? {};
                    const messages: string[] = [];
                    Object.entries<Record<string, any>>(errors).forEach(([, arr]) => {
                        arr.forEach((msg: string) => messages.push(msg));
                    });
                    showErrorMessages(messages);
                } catch {
                    showErrorMessages(['Error handling response']);
                }
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            })
        }

        if (contracts.length > 0) {            
            let terminalContract = dummyContract;
            terminalContract.propertyNames = contracts[0]?.propertyNames;
            terminalContract.poi = contracts[0]?.poi;
            terminalContract.terminalDescription = contracts[0]?.terminalDescription;
            terminalContract.terminalId = contracts[0]?.terminalId;

            if (!terminalContract.propertyNames) {
                getPropertyInfo(keycloak.token || '', contracts[0]?.poi).then((result) => {
                    terminalContract.propertyNames = result;
                    setNewContract(terminalContract);
                })
            }
            else {
                setNewContract(terminalContract);
            }                    
        }               
    }

    const init = (isCancelled: () => boolean = () => false) => {
        handlePaging(0, isCancelled);
    };

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
        setTableBodyHeight(h - 284);
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);

        let cancelled = false;

        if (contracts?.length == 0) {
            init(() => cancelled);
            onResize();
        }

        return () => {
            cancelled = true;
            window.removeEventListener("resize", onResize);
        };

    }, [formData, filterData, terminal.Id]);

    return (
        <> 
            <Row className="">                  
                <Col className="">
                    <Card className="rounded">                        
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="">
                                <Col sm="12" className="">
                                    <h4 className="mb-2 qvend-title">{t('terminalcontract')} - Terminal {terminal.description} {terminal!.poi} </h4>                                    
                                </Col>
                            </Row>
                            <Row style={{ fontSize: '11px' }}>
                                <Col md={3} xs={5} style={{ paddingRight: 0 }}>                                    
                                </Col>
                                <Col md={3} xs={1}>                                    
                                </Col>
                                <Col className="text-end">
                                    {(<button type="button" disabled={getUsedBrands().length >= 7} className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.5rem', margin: '5px' }} onClick={() => { handleAddContract() }}>+</button>)}                                    
                                </Col>
                            </Row>                            
                            <Row>
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={handlePaging} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll" }}>
                                                <Col md={3} xs={3} className="qvend-table table-column-first">{t('terminalcontracts.brand')}</Col>
                                                <Col md={3} xs={3} className="qvend-table">{t('terminalcontracts.tid')}</Col>
                                                <Col md={3} xs={3} className="qvend-table table-column-first">{t('terminalcontracts.mid')}</Col>
                                                <Col md={3} xs={3} className="qvend-table">{t('terminalcontracts.host')}</Col>                                                
                                            </Row>                                            
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>                                            
                                            {drafts.length > 0 && drafts.map((draft, index) => (
                                                <Link
                                                    to="#" onClick={() => handleRowClick(draft)}                                                     
                                                    key={draft.key}
                                                >
                                                    <TerminalContracts
                                                        key={draft.key}
                                                        terminalContract={draft.current}
                                                        rowColor={selectedId === draft.key ? "gray" : draft.current.warnings && draft.current.warnings.length > 0 && draft.current.id > 0 ? "red" : index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                    />
                                                </Link>
                                            ))}                                           
                                        </div>                                        
                                    </div>
                                </Col>                              
                            </Row>
                            <Row className="d-none errorMsg">
                                    <Col>
                                        {
                                            errorMsg.map((item, index) => {
                                                return <Alert key={index} variant="warning" className="alert" role="alert">
                                                    <span>{item}</span>
                                                </Alert>;
                                            })
                                        }
                                    </Col>
                                </Row>
                            <Row>
                                <ListPagination
                                    articlesCount={contractCount}
                                    currentPage={currentPage}
                                    onSetPage={handlePaging}
                                    onRefresh={handleRefreshButton} />
                            </Row>
                            <Row style={{ paddingTop: "3px" }}>
                                <Col>
                                    <button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px', backgroundColor: "red" }} onClick={() => { handleCancel() }}>{t("button.back")}</button>                                    
                                </Col>
                                <Col className="text-end">
                                    <button type="button" className="btn btn-fixed-width" disabled={drafts.filter(d => hasContractChanged(d)).length === 0} style={{ fontSize: '11px' }} onClick={() => {  handleSaveAll() }}>{t("button.save")}</button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>                                    
            </Row>
        </>
    )
}

export default LinkContractToTerminal